import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS, es, fr } from "date-fns/locale";
import { useLocaleProvider } from "../../../../components/locale-provider";
import { StyledDateFieldWrapper } from "./DateField.styles";

/**
 * Resolves the active `LocaleProvider` locale (`es-ES`/`en-US`/`fr-FR`) to a `date-fns` `Locale`
 * object for `react-datepicker`'s month/weekday names. Mirrors the mapping already used by
 * `shared/widgets/modificationPriceManagement/ExtraSelection.tsx` (lines 61-68).
 */
function getDateFnsLocaleFor(locale: string) {
  if (locale.startsWith("fr")) {
    return fr;
  }
  if (locale.startsWith("en")) {
    return enUS;
  }
  return es;
}

export interface DateFieldProps {
  id: string;
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  isClearable?: boolean;
  placeholder?: string;
}

/**
 * A thin, module-local wrapper around `react-datepicker`'s single-date `DatePicker`, fixed to
 * `dd/MM/yyyy` and locale-aware for month/weekday names. Distinct from the date-range picker used
 * by HU1's filters (US-008) - this component always picks exactly one date.
 */
export function DateField({
  id,
  selected,
  onChange,
  minDate,
  maxDate,
  isClearable,
  placeholder,
}: DateFieldProps) {
  const localeProvider = useLocaleProvider();
  const dateFnsLocale = getDateFnsLocaleFor(localeProvider.currentLocale);

  return (
    <StyledDateFieldWrapper>
      <DatePicker
        id={id}
        selected={selected}
        onChange={onChange}
        dateFormat="dd/MM/yyyy"
        locale={dateFnsLocale}
        minDate={minDate}
        maxDate={maxDate}
        isClearable={isClearable}
        placeholderText={placeholder ?? "dd/mm/yyyy"}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        className="form-control"
      />
    </StyledDateFieldWrapper>
  );
}
